import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box, Dialog, DialogContent } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  AddOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import PlaceApi from "Services/Place";
import AddPlaceForm from "./AddPlaceForm";
import GoogleMapLoader from "../../Googlemaploader";

const Place = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addPlaceModal, setAddPlaceModal] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState("");

  const normalizePlaceList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.data)) {
      return {
        rows: payload?.data?.data || [],
        total: payload?.data?.totalRecords || payload?.data?.data?.length || 0,
      };
    }
    if (Array.isArray(payload?.data)) {
      return {
        rows: payload?.data || [],
        total: payload?.totalRecords || payload?.data?.length || 0,
      };
    }
    return { rows: [], total: 0 };
  };

  const getPlaces = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      const normalized = normalizePlaceList(res);
      setTotalPages(normalized?.total || 1);
      setRow(normalized?.rows || []);
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    PlaceApi.getPlaceList({ page, limit: pageSize, search: searchText }).then(onSuccess, onFailure);
  }, [dispatch, page, pageSize, searchText]);

  const getAllPlaces = useCallback(() => {
    const onSuccess = (res) => {
      const normalized = normalizePlaceList(res);
      setbulkrow(normalized?.rows || []);
    };
    const onFailure = () => setbulkrow([]);
    PlaceApi.getPlaceList({ page: 1, limit: "unlimited", search: "" }).then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    getPlaces();
    getAllPlaces();
  }, [getPlaces, getAllPlaces]);

  const calculateSno = (pageIndex, pageLimit, index) => {
    return (pageIndex - 1) * pageLimit + index + 1;
  };

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || data?._id || index + 1,
    sno: calculateSno(page, pageSize, index),
    placeId: data?.id || data?._id || "-",
    name: data?.name || "-",
    type: data?.type || "-",
    locality: data?.addressLocality || "-",
    country: data?.addressCountry || "-",
    category: data?.propertiesCategory || "-",
    source: data?.source || "-",
    status: data?.status || "-",
    coords:
      data?.centroidLat !== null && data?.centroidLon !== null
        ? `${data?.centroidLat}, ${data?.centroidLon}`
        : "-",
    raw: data,
  }));

  const formatteddatas = dataRows.map((r) => ({
    "S.NO": r?.sno,
    "Place ID": r?.placeId,
    Name: r?.name,
    Type: r?.type,
    Locality: r?.locality,
    Country: r?.country,
    Category: r?.category,
    Source: r?.source,
    Status: r?.status,
    Coordinates: r?.coords,
  }));

  const bulkdataRows = _.map(_.orderBy(bulkrow || [], ["createdAt"], ["asc"]), (data, index) => ({
    sno: index + 1,
    placeId: data?.id || data?._id || "-",
    name: data?.name || "-",
    type: data?.type || "-",
    locality: data?.addressLocality || "-",
    country: data?.addressCountry || "-",
    category: data?.propertiesCategory || "-",
    source: data?.source || "-",
    status: data?.status || "-",
    coords:
      data?.centroidLat !== null && data?.centroidLon !== null
        ? `${data?.centroidLat}, ${data?.centroidLon}`
        : "-",
  }));

  const bulkformatteddatas = bulkdataRows.map((r) => ({
    "S.NO": r?.sno,
    "Place ID": r?.placeId,
    Name: r?.name,
    Type: r?.type,
    Locality: r?.locality,
    Country: r?.country,
    Category: r?.category,
    Source: r?.source,
    Status: r?.status,
    Coordinates: r?.coords,
  }));

  const handlePageChange = (newPage) => {
    const theNewPage = parseInt(newPage, 10) + 1;
    if (theNewPage >= 1) setPage(theNewPage);
  };

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleCopy = () => {
    const header = ["S.NO", "Place ID", "Name", "Type", "Locality", "Country", "Category", "Source", "Status", "Coordinates"].join("\t");
    const rows = dataRows
      .map((r) => [r?.sno, r?.placeId, r?.name, r?.type, r?.locality, r?.country, r?.category, r?.source, r?.status, r?.coords].join("\t"))
      .join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Places");
    XLSX.writeFile(workbook, "PlaceList.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "PlaceList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((r) => [r?.sno, r?.placeId, r?.name, r?.type, r?.locality, r?.country, r?.category, r?.source, r?.status, r?.coords]);
    doc.autoTable({
      head: [["S.NO", "Place ID", "Name", "Type", "Locality", "Country", "Category", "Source", "Status", "Coordinates"]],
      body: tableData,
    });
    doc.save("PlaceList.pdf");
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All PlaceList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddPlace = () => {
    setEditingPlaceId("");
    setAddPlaceModal(true);
  };

  const handleCloseAddPlaceModal = () => {
    setAddPlaceModal(false);
    setEditingPlaceId("");
  };

  const handleAddPlaceSuccess = () => {
    setAddPlaceModal(false);
    getPlaces();
    getAllPlaces();
  };

  const handleEditPlace = (placeData) => {
    const placeId = placeData?.id || placeData?._id;
    if (!placeId) {
      dispatch(
        showSnackbar({
          message: "Place id introuvable",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "warning",
        })
      );
      return;
    }
    setEditingPlaceId(placeId);
    setAddPlaceModal(true);
  };

  const handleDeletePlace = (placeData) => {
    const placeId = placeData?.id || placeData?._id;
    if (!placeId) {
      dispatch(
        showSnackbar({
          message: "Place id introuvable",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "warning",
        })
      );
      return;
    }

    const allowDelete = window.confirm("Voulez-vous vraiment supprimer ce place ?");
    if (!allowDelete) return;

    dispatch(showLoader("Deleting place..."));
    PlaceApi.deletePlaceById(placeId).then(
      (res) => {
        dispatch(hideLoader());
        if (res?.status === 200 || res?.status === 204) {
          dispatch(
            showSnackbar({
              message: res?.data?.message || "Place supprime avec succes",
              autoHideDuration: 2500,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "success",
            })
          );
          getPlaces();
          getAllPlaces();
        }
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Erreur lors de la suppression du place",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  };

  const getStatusBadgeStyle = (value) => {
    const key = String(value || "").toLowerCase();
    const map = {
      approved: { bg: "#14b8a6", fg: "#ffffff" },
      active: { bg: "#10b981", fg: "#ffffff" },
      published: { bg: "#10b981", fg: "#ffffff" },
      confirmed: { bg: "#10b981", fg: "#ffffff" },
      completed: { bg: "#0ea5e9", fg: "#ffffff" },
      onboarding: { bg: "#ef4444", fg: "#ffffff" },
      boarding: { bg: "#ef4444", fg: "#ffffff" },
      cancelled: { bg: "#ef4444", fg: "#ffffff" },
      rejected: { bg: "#ef4444", fg: "#ffffff" },
      pending: { bg: "#f59e0b", fg: "#111827" },
      draft: { bg: "#6b7280", fg: "#ffffff" },
      full: { bg: "#7c3aed", fg: "#ffffff" },
      departed: { bg: "#2563eb", fg: "#ffffff" },
      arrived: { bg: "#14b8a6", fg: "#ffffff" },
      inactive: { bg: "#9ca3af", fg: "#111827" },
    };
    return map[key] || { bg: "#64748b", fg: "#ffffff" };
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    /* { field: "placeId", headerName: "Place ID", minWidth: 220, sortable: true }, */
    { field: "name", headerName: "Name", minWidth: 160, sortable: true },
    { field: "type", headerName: "Type", minWidth: 100, sortable: true },
    { field: "locality", headerName: "Locality", minWidth: 130, sortable: true },
    { field: "country", headerName: "Country", minWidth: 130, sortable: true },
    { field: "category", headerName: "Category", minWidth: 130, sortable: true },
    { field: "source", headerName: "Source", minWidth: 120, sortable: true },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return (
          <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>
            {String(params?.value || "-")}
          </span>
        );
      },
    },
    { field: "coords", headerName: "Coordinates", minWidth: 160, sortable: false },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            style={{ minWidth: 36, padding: "4px 8px" }}
            onClick={() => handleEditPlace(params?.row?.raw)}
          >
            <Edit fontSize="small" />
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }}
            onClick={() => handleDeletePlace(params?.row?.raw)}
          >
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button>
      </Grid>
      <Grid item xs={2} md={2} lg={2} xl={2} className={classes.funBtn}>
        <Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button>
      </Grid>
      <Grid item xs={6} md={6} lg={6} xl={6} className={classes.addBtn}>
        <Button variant="contained" color="primary" startIcon={<AddOutlined />} onClick={handleAddPlace}>
          Add Place
        </Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => handlePageChange(newPage)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => {
              const value = model?.quickFilterValues?.join(" ") || "";
              setSearchText(value);
            }}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>

      <Dialog open={addPlaceModal} onClose={handleCloseAddPlaceModal} fullWidth maxWidth="xl">
        <DialogContent sx={{ p: 0 }}>
          <GoogleMapLoader>
            <AddPlaceForm
              onSuccess={handleAddPlaceSuccess}
              onCancel={handleCloseAddPlaceModal}
              placeId={editingPlaceId}
            />
          </GoogleMapLoader>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default Place;
